import {
    ActionIcon,
    Box,
    Collapse,
    Flex,
    Select,
    Title,
    Tooltip,
    useMantineTheme,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { IconInfoCircle } from "@tabler/icons-react";
import { useFeature } from "flagged";
import { useCallback, useMemo, useState } from "react";
import { DatasourceFormattedText } from "src/components/DatasourceFormattedText";
import { DocumentViews } from "src/components/DocumentGraph/DocumentViews";
import { hasFacetFields } from "src/components/DocumentGraph/services/hasFacetFields";
import { selectOverviewFields } from "src/components/DocumentGraph/services/overviewFields";
import { ColumnType } from "src/db";
import { useDatasourceOverview, useDatasources } from "src/hooks/useDatasources";
import { useAvailableAlwaysOnDatasourceIds, useUserDatasources } from "src/hooks/useUser";
import {
    type DatasourceKind,
    visibleDatasources,
} from "src/services/datasources/visibleDatasources";

export const DocumentsRoute = ({
    index,
    setSelectedIndex,
    kind = "document",
}: {
    index: string;
    setSelectedIndex: (value: string) => void;
    kind?: DatasourceKind;
}) => {
    const theme = useMantineTheme();
    const assetsViewerEnabled = Boolean(useFeature("ASSETS_VIEWER"));
    const [infoOpened, { toggle: toggleInfo }] = useDisclosure(false);
    const [, setFilteredDocumentCount] = useState<number | undefined>(undefined);
    const [datasourceSearch, setDatasourceSearch] = useState("");
    const { data: chatVectorIndex = [] } = useDatasources();
    const { data: userDatasourceDetails } = useUserDatasources();
    const availableAlwaysOnIds = useAvailableAlwaysOnDatasourceIds();
    const getDisplayLabel = useCallback((label: string, datasourceLabel: string) => {
        const normalize = (s: string) => s.toLowerCase().trim();
        return normalize(label) !== normalize(datasourceLabel)
            ? `${datasourceLabel} (${label})`
            : label;
    }, []);

    const titles = useMemo(
        () =>
            Object.fromEntries(
                chatVectorIndex.map((o) => [o.id, getDisplayLabel(o.label, o.datasource_label)]),
            ) as Record<string, string>,
        [chatVectorIndex, getDisplayLabel],
    );

    // Role-granted datasource ids, sourced live from /v1/user (via useUserDatasources)
    // rather than the one-time-synced "userDatasources" localStorage snapshot, so
    // access changes (role updates, approved access requests, saved preferences)
    // are reflected without requiring a full page reload.
    const availableUserDatasourceIds = useMemo(
        () => (userDatasourceDetails ?? []).filter((ds) => ds.has_access).map((ds) => ds.id),
        [userDatasourceDetails],
    );

    const getAvailableIndicesInUserRoles = useCallback(
        () =>
            visibleDatasources({
                all: chatVectorIndex,
                userRoles: availableUserDatasourceIds,
                kind,
                requireIndex: true,
                availableAlwaysOnIds,
                assetsViewerEnabled,
            }),
        [
            chatVectorIndex,
            availableUserDatasourceIds,
            kind,
            availableAlwaysOnIds,
            assetsViewerEnabled,
        ],
    );

    const { data: datasourceOverview } = useDatasourceOverview();

    const currentDatasource = chatVectorIndex.find((ds) => ds.id === index);
    const overviewFields = useMemo(
        () => selectOverviewFields(currentDatasource?.index_description ?? []),
        [currentDatasource],
    );
    const dateFieldDescription = useMemo(
        () => currentDatasource?.index_description.find((field) => field.type === ColumnType.DATE),
        [currentDatasource],
    );
    const ingestionStatusForDatasource = Array.isArray(datasourceOverview)
        ? datasourceOverview.find(
              (value) => value.id === (currentDatasource?.datasource_id ?? index),
          )
        : undefined;

    return (
        <Flex
            px="xl"
            py="md"
            direction="column"
            gap="xs"
            w="100%"
            maw="100%"
            style={{ minWidth: 0, overflowX: "hidden" }}
        >
            <Flex justify="space-between" gap="md" w="100%" style={{ minWidth: 0 }}>
                <Flex align="baseline" gap="xs" wrap="wrap" style={{ minWidth: 0, flex: 1 }}>
                    <Title
                        lh={2}
                        c="purple.8"
                        order={2}
                        style={{ textTransform: "uppercase", minWidth: 0 }}
                    >
                        {titles[index]}
                    </Title>
                    {ingestionStatusForDatasource && (
                        <Tooltip label="Show ingestion status and description">
                            <ActionIcon
                                aria-label="Show ingestion status and description"
                                size="sm"
                                variant="subtle"
                                color="gray"
                                onClick={toggleInfo}
                            >
                                <IconInfoCircle size={16} />
                            </ActionIcon>
                        </Tooltip>
                    )}
                </Flex>
                <Box mt="xs" maw={300} style={{ flexShrink: 0 }}>
                    <Select
                        id="data-sources"
                        value={index}
                        searchValue={datasourceSearch}
                        onSearchChange={setDatasourceSearch}
                        onDropdownOpen={() => setDatasourceSearch("")}
                        onChange={(value: string | null) => {
                            if (value) {
                                setSelectedIndex(value);
                            }
                        }}
                        data={getAvailableIndicesInUserRoles().map((item) => ({
                            label: getDisplayLabel(item.label, item.datasource_label),
                            value: item.id,
                        }))}
                        placeholder="Select a Knowledge Base"
                        searchable
                        nothingFoundMessage="No Knowledge Bases found"
                        styles={{
                            input: {
                                fontWeight: "bold",
                                color: theme.colors.purple[7],
                            },
                            dropdown: { maxHeight: 200, overflowY: "auto" },
                        }}
                        withScrollArea={false}
                        size="sm"
                        w={300}
                    />
                </Box>
            </Flex>
            {ingestionStatusForDatasource && (
                <Collapse in={infoOpened}>
                    <Flex direction="column" gap="sm" w="100%" maw="100%" style={{ minWidth: 0 }}>
                        {ingestionStatusForDatasource.description && (
                            <DatasourceFormattedText
                                label="Description"
                                value={ingestionStatusForDatasource.description}
                            />
                        )}
                        {ingestionStatusForDatasource.ingestion_status && (
                            <DatasourceFormattedText
                                label="Ingestion Status"
                                value={ingestionStatusForDatasource.ingestion_status}
                            />
                        )}
                    </Flex>
                </Collapse>
            )}
            <Box w="100%" maw="100%" style={{ minWidth: 0 }}>
                <DocumentViews
                    key={index}
                    index={index}
                    namespace={currentDatasource?.index_id}
                    datasourceId={currentDatasource?.datasource_id}
                    hasFacets={hasFacetFields(currentDatasource?.index_description ?? [])}
                    assetMode={kind === "asset"}
                    rootLabel={titles[index] ?? index}
                    datasourceLabel={titles[index] ?? index}
                    fields={overviewFields}
                    dateField={dateFieldDescription?.name}
                    dateFieldLabel={dateFieldDescription?.header_name}
                    onFilteredCountChange={setFilteredDocumentCount}
                />
            </Box>
        </Flex>
    );
};
